/**
 * Fecha y hora de Argentina (UTC−3; no tiene horario de verano) para los campos datetime-local y las columnas con hora:
 * se muestran y se cargan siempre en hora de Argentina, aunque la PC tenga otra zona horaria. El backend recibe ISO UTC.
 */

const OFFSET_AR_MS = 3 * 60 * 60 * 1000;

/**
 * Hasta cuántos días atrás puede ser un hecho cargado a mano ("Contado el", la fecha y hora de un movimiento manual).
 * Refleja MAX_DIAS_ATRAS_HECHO de backend/src/wms/momento-stock.ts (el backend es el que valida): si cambia allá,
 * cambiar acá.
 */
export const MAX_DIAS_ATRAS_HECHO = 7;
const LOCAL_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

const comoFecha = (v: string | Date) => (v instanceof Date ? v : new Date(v));
/** Los campos UTC de este Date son la hora de Argentina. */
const enAr = (v: string | Date) => new Date(comoFecha(v).getTime() - OFFSET_AR_MS);
const dos = (n: number) => String(n).padStart(2, '0');

/** Valor de un input datetime-local (AAAA-MM-DDTHH:MM) con la hora de Argentina del instante. */
export function aLocalAr(v: string | Date): string {
  const d = enAr(v);
  return `${d.getUTCFullYear()}-${dos(d.getUTCMonth() + 1)}-${dos(d.getUTCDate())}T${dos(d.getUTCHours())}:${dos(d.getUTCMinutes())}`;
}

/** Ahora, como valor de un datetime-local en hora de Argentina (el max de los campos de un hecho). */
export const ahoraLocalAr = (ahora: Date = new Date()) => aLocalAr(ahora);

/** Lo más viejo que se acepta para un hecho (el min de sus campos datetime-local): hace MAX_DIAS_ATRAS_HECHO días. */
export const minHechoLocalAr = (ahora: Date = new Date()) => aLocalAr(new Date(ahora.getTime() - MAX_DIAS_ATRAS_HECHO * 24 * 60 * 60 * 1000));

/** Un valor de datetime-local (hora de Argentina) como ISO UTC; vacío o inválido: null. */
export function localArAIso(v: string): string | null {
  const m = LOCAL_RE.exec((v ?? '').trim());
  if (!m) return null;
  const utc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6] ?? 0));
  return Number.isNaN(utc) ? null : new Date(utc + OFFSET_AR_MS).toISOString();
}

/** dd/mm/aaaa HH:MM en hora de Argentina; sin valor, —. */
export function fechaHoraAr(v: string | Date | null | undefined): string {
  if (!v) return '—';
  const d = enAr(v);
  return `${dos(d.getUTCDate())}/${dos(d.getUTCMonth() + 1)}/${d.getUTCFullYear()} ${dos(d.getUTCHours())}:${dos(d.getUTCMinutes())}`;
}

/** HH:MM en hora de Argentina. */
export function horaAr(v: string | Date): string {
  const d = enAr(v);
  return `${dos(d.getUTCHours())}:${dos(d.getUTCMinutes())}`;
}

/** Mismo minuto (lo que se ve en pantalla). */
export const mismoMinuto = (a: string | Date, b: string | Date) => aLocalAr(a) === aLocalAr(b);

/** Mismo día de Argentina. */
export const mismoDiaAr = (a: string | Date, b: string | Date) => aLocalAr(a).slice(0, 10) === aLocalAr(b).slice(0, 10);
